import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  SIMPLY_FIT_INSTAGRAM_URL,
  getTrainerLiveStatus,
  getTrainerStatusAction,
  getTrainerStatusKey,
  shouldShowTrainerStatus,
} from '../../util/trainerStatus';

import NamedLink from '../NamedLink/NamedLink';

import css from './TrainerStatusStrip.module.css';

/**
 * Routes where the full `PendingApprovalBanner` already speaks for itself.
 * The strip stands down on all of them so the two can never show together.
 *
 * This is a whole-route rule on purpose. Matching only the trainer’s own listing
 * id would let the strip appear beside the banner whenever the two disagreed
 * about which listing is theirs — the exact double message this pair exists to
 * prevent. The cost is that a trainer browsing another coach’s profile sees no
 * strip on that one page, which is the trade the spec asks for.
 */
const BANNER_PAGES = [
  'ListingPage',
  'ListingPageVariant',
  'ListingPageCanonical',
  'ListingBasePage',
  'EditListingPage',
  'EditListingStripeOnboardingPage',
  'NewListingPage',
];

/**
 * True when the full banner owns this page, so the strip must not render.
 */
export const isBannerSurface = currentPage => BANNER_PAGES.includes(currentPage);

/**
 * The call-to-action at the end of the strip. Identical to the full banner in
 * every state — same destinations, same wording — so the two surfaces can never
 * send a trainer to different places for the same status.
 */
const StripAction = props => {
  const { status, listingParams } = props;
  const action = getTrainerStatusAction(status);

  if (action === 'plan') {
    return (
      <NamedLink
        className={css.button}
        name="SubscriptionsPage"
        params={{ id: listingParams.id, slug: listingParams.slug }}
      >
        <FormattedMessage id="TrainerStatusStrip.getPlanButton" />
      </NamedLink>
    );
  }

  if (action === 'instagram') {
    return (
      <a
        className={css.button}
        href={SIMPLY_FIT_INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FormattedMessage id="TrainerStatusStrip.verifyButton" />
      </a>
    );
  }

  if (action === 'upgrade') {
    return (
      <NamedLink
        className={css.button}
        name="SubscriptionsPage"
        params={{ id: listingParams.id, slug: listingParams.slug }}
      >
        <FormattedMessage id="TrainerStatusStrip.upgradeButton" />
      </NamedLink>
    );
  }

  return null;
};

/**
 * One-line sitewide reminder of how far a trainer is from going live.
 *
 * This is the condensed half of a pair: the full `PendingApprovalBanner` covers
 * the trainer's own listing page and the Edit Listing wizard, and this strip
 * covers every other page. Both read the same `getTrainerLiveStatus`, so they
 * can never say two different things about the same trainer, and they never
 * appear on the same page.
 *
 * Renders nothing for trainees, for signed-out visitors, for trainers with no
 * listing yet, and for trainers who are verified on Tier 2/3 — at that point
 * the profile is live and there is nothing left to nudge about.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - extends the root class
 * @param {string} [props.rootClassName] - overrides the root class
 * @param {Object} props.currentUser
 * @param {Object} props.profileListing - the trainer's own listing as
 *   `{ id, slug, metadata }`, kept in the user duck so every page has it
 * @param {string} props.currentPage - resolved route name of the current page
 * @returns {JSX.Element|null}
 */
const TrainerStatusStrip = props => {
  const { className, rootClassName, currentUser, profileListing, currentPage } = props;

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (!currentUser?.id || userType === 'trainee' || !profileListing) {
    return null;
  }

  if (isBannerSurface(currentPage)) {
    return null;
  }

  const status = getTrainerLiveStatus(profileListing);
  if (!shouldShowTrainerStatus(status)) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes} role="status">
      <span className={css.text}>
        <FormattedMessage id={`TrainerStatusStrip.text_${getTrainerStatusKey(status)}`} />
      </span>
      <StripAction status={status} listingParams={profileListing} />
    </div>
  );
};

export default TrainerStatusStrip;
